import {Routes} from '@angular/router';
import {AuthLayoutComponent} from '@/layout/auth-layout/auth-layout.component';
import {authGuard, guestGuard} from './core/auth/auth-guard';
import {permissionGuard} from './core/auth/permission-guard';

export const routes: Routes = [
    {
        path: 'auth',
        component: AuthLayoutComponent,
        canActivate: [guestGuard],
        children: [
            {
                path: '',
                loadChildren: () => import('@/features/auth/auth.routes'),
            },
        ],
    },
    {
        path: 'create-password',
        component: AuthLayoutComponent,
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('@/features/auth/components/create-password/create-password.component').then(
                        (m) => m.CreatePasswordComponent
                    ),
            },
        ],
    },
    {
        path: 'reset-password',
        component: AuthLayoutComponent,
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('@/features/auth/components/reset-password/reset-password.component').then(
                        (m) => m.ResetPasswordComponent
                    ),
            },
        ],
    },
    {
        path: '',
        // Lazy: the shell (sidebar, tooltips, icons) isn't needed on the login screens.
        loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
        canActivateChild: [authGuard],
        children: [
            {path: '', pathMatch: 'full', redirectTo: 'dashboard'},
            {
                // Open to every logged-in user (no permission needed).
                path: 'dashboard',
                loadChildren: () => import('./features/dashboard/dashboard.routes'),
            },
            {
                path: 'projects',
                canActivate: [permissionGuard],
                data: {module: 'Projects'},
                loadChildren: () => import('./features/projects/projects.routes'),
            },
            {
                path: 'boq',
                canActivate: [permissionGuard],
                data: {module: 'BOQ'},
                loadChildren: () => import('./features/projects/boq.routes'),
            },
            {
                path: 'sites',
                canActivate: [permissionGuard],
                data: {module: 'Sites'},
                loadChildren: () => import('./features/sites/sites.routes'),
            },
            {
                path: 'subcontractors',
                canActivate: [permissionGuard],
                data: {module: 'SubContractors'},
                loadChildren: () => import('./features/sub-contractors/sub-contractors.routes'),
            },
            {
                path: 'sub-contractor-category-master',
                canActivate: [permissionGuard],
                data: {module: 'SubContractorCategoryMaster'},
                loadChildren: () => import('./features/sub-contractor-categories/sub-contractor-category-master.routes'),
            },
            {
                path: 'users',
                canActivate: [permissionGuard],
                data: {module: 'UserList'},
                loadChildren: () => import('./features/employees/user-list.routes'),
            },
            {
                path: 'employees',
                canActivate: [permissionGuard],
                data: {module: 'Employees'},
                loadChildren: () => import('./features/employees/employees.routes'),
            },
            {
                path: 'employee-project-mapping',
                canActivate: [permissionGuard],
                data: {module: 'EmployeeProjectMapping'},
                loadChildren: () => import('./features/employees/employee-project-mapping.routes'),
            },
            {
                path: 'material-mapping',
                canActivate: [permissionGuard],
                data: {module: 'MaterialMapping'},
                loadChildren: () => import('./features/materials/material-mapping.routes'),
            },
            {
                path: 'materials',
                canActivate: [permissionGuard],
                data: {module: 'Materials'},
                loadChildren: () => import('./features/materials/materials.routes'),
            },
            {
                path: 'unit-master',
                canActivate: [permissionGuard],
                data: {module: 'UnitMaster'},
                loadChildren: () => import('./features/material-masters/unit-master.routes'),
            },
            {
                path: 'material-category-master',
                canActivate: [permissionGuard],
                data: {module: 'MaterialCategoryMaster'},
                loadChildren: () => import('./features/material-masters/material-category-master.routes'),
            },
            {
                path: 'material-sub-category-master',
                canActivate: [permissionGuard],
                data: {module: 'MaterialSubCategoryMaster'},
                loadChildren: () => import('./features/material-masters/material-sub-category-master.routes'),
            },
            {
                path: 'consumption-category-master',
                canActivate: [permissionGuard],
                data: {module: 'ConsumptionCategoryMaster'},
                loadChildren: () => import('./features/material-masters/consumption-category-master.routes'),
            },
            {
                path: 'material-inward',
                canActivate: [permissionGuard],
                data: {module: 'MaterialInward'},
                loadChildren: () => import('./features/materials/material-inward.routes'),
            },
            {
                path: 'stock-tracking',
                canActivate: [permissionGuard],
                data: {module: 'StockTracking'},
                loadChildren: () => import('./features/materials/stock-tracking.routes'),
            },
            {
                path: 'consumption',
                canActivate: [permissionGuard],
                data: {module: 'Consumption'},
                loadChildren: () => import('./features/cost/consumption.routes'),
            },
            {
                path: 'salary-master',
                canActivate: [permissionGuard],
                data: {module: 'Salary'},
                loadChildren: () => import('./features/cost/salary.routes'),
            },
            {
                path: 'language-master',
                canActivate: [permissionGuard],
                data: {module: 'LanguageMaster'},
                loadChildren: () => import('./features/configuration/language-master.routes'),
            },
            {
                path: 'language-config',
                canActivate: [permissionGuard],
                data: {module: 'LanguageConfig'},
                loadChildren: () => import('./features/configuration/language-config.routes'),
            },
            {
                path: 'profit-loss',
                canActivate: [permissionGuard],
                data: {module: 'ProfitLossAnalysis'},
                loadChildren: () => import('./features/reports/profit-loss.routes'),
            },
            {
                path: 'reports',
                loadChildren: () => import('./features/reports/reports.routes'),
            },
            {
                path: 'audit-log',
                loadChildren: () => import('./features/audit/audit.routes'),
            },
            {
                path: 'bulk-upload',
                loadChildren: () => import('./features/bulk-upload/bulk-upload.routes'),
            },
        ],
    },
    {
        path: 'no-access',
        canActivate: [authGuard],
        loadComponent: () =>
            import('@/features/auth/components/no-access/no-access.component').then((m) => m.NoAccessComponent),
    },
    {path: '**', redirectTo: ''},
];
